import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import Actions from "../../../../Actions";
import logger from "../../../../utils/LoggerFrontend";
import ContinueButton from "../../../../Components/ContinueButton/ContinueButton";

import { QUESTIONS_TYPES } from "./QuestionsMapping.constants";
import {
  BoldTitle,
  customStyles,
  FlexContainer,
  FlexRowContainer,
  PlainQuestion,
  Wrapper
} from "./QuestionsMapping.styles";
import { buildMergedQuestions } from "./QuestionMapping.helpers";

export const QuestionsMapping = ({
  offerId,
  campaignId,
  orgaId,
  currentlySelected,
  currentQuestionMapping = {}
}) => {
  const [offer, setOffer] = useState(null);
  const [ats, setAts] = useState(null);
  const [questionMapping, setQuestionMapping] = useState(currentQuestionMapping);
  const { t } = useTranslation();

  logger.log("QuestionsMapping", "QuestionsMapping called", {
    offerId,
    campaignId,
    orgaId,
    currentlySelected,
    currentQuestionMapping
  });

  useEffect(() => {
    const getRecruiteeJobByIdCompleted = Actions.getRecruiteeJobById.completed.listen((data) => {
      logger.log("QuestionsMapping", "fetched recruitee job", { data });
      const offer = data.payload;
      setOffer(offer);
      getRecruiteeJobByIdCompleted();
    });

    const getRecruiteeJobByIdFailed = Actions.getRecruiteeJobById.failed.listen(({ error }) => {
      logger.log("error:QuestionsMapping", "unbable to fetch recruitee jobs", { error });
      Actions.addNotification("Ladefehler", "error");
      getRecruiteeJobByIdFailed();
    });

    const getAtsCompleted = Actions.getAtsById.completed.listen((data) => {
      logger.log("QuestionsMapping", "fetched ats", { data });
      const ats = data.payload;
      setAts(ats);
      getAtsCompleted();
    });

    const getAtsFailed = Actions.getAtsById.failed.listen(({ error }) => {
      logger.log("error:QuestionsMapping", "unable to fetch ats", { error });
      Actions.addNotification("Ladefehler", "error");
      getAtsFailed();
    });

    if (currentlySelected) {
      Actions.getRecruiteeJobById(offerId, orgaId);
      Actions.getAtsById(campaignId);
    }

    return () => {
      getRecruiteeJobByIdCompleted();
      getRecruiteeJobByIdFailed();
      getAtsCompleted();
      getAtsFailed();
    };
  }, [offerId, campaignId, currentlySelected]);

  const onSaveMappingQuestion = () => {
    const saveQuestionsMappingCompleted = Actions.saveQuestionsMapping.completed.listen(() => {
      Actions.addNotification("Synchronisierung erfolgreich");
    });

    const saveQuestionsMappingFailed = Actions.saveQuestionsMapping.failed.listen((error) => {
      logger.log("error:QuestionsMapping", "unable to connect with recruitee job", {
        error
      });
      Actions.addNotification("Synchronisierung nicht erfolgreich", "error");
    });

    const data = {
      integrations: {
        recruitee: {
          questionMapping
        }
      }
    };
    Actions.saveQuestionsMapping(campaignId, data);

    return () => {
      saveQuestionsMappingCompleted();
      saveQuestionsMappingFailed();
    };
  };

  const handleMappingChange = (questionId, option, type) => {
    if (option === null) {
      questionMapping[questionId] = null;
      const newQuestionMapping = {
        ...questionMapping
      };

      setQuestionMapping(newQuestionMapping);
    } else {
      const newQuestionMapping = {
        ...questionMapping,
        ...(option && option.value
          ? { [questionId]: { integrationId: option.value, questionType: type } }
          : {})
      };

      setQuestionMapping(newQuestionMapping);
    }
  };

  const renderQuestionsAndDropdowns = (mergedQuestions = [], questions = [], type) => {
    return questions.map((question, index) => {
      const selectedQuestion = mergedQuestions
        .map((mergedQuestion) =>
          mergedQuestion.options.find(
            (q) => q.value === questionMapping[question?.id]?.integrationId
          )
        )
        .find((q) => q !== undefined);

      const placeholder = mergedQuestions[index]?.body || "-";
      return (
        <>
          <FlexRowContainer>
            <PlainQuestion key={index}>{question.question}</PlainQuestion>
            <div key={index} style={{ marginBottom: "20px", width: "100%" }}>
              <Select
                styles={customStyles}
                options={mergedQuestions}
                onChange={(option) => handleMappingChange(question.id, option, type)}
                value={selectedQuestion}
                placeholder={placeholder}
                isClearable
              />
            </div>
          </FlexRowContainer>
        </>
      );
    });
  };

  if (!currentlySelected) {
    return null;
  }

  if (!offer) {
    return (
      <Wrapper>
        <h3>Loading offer</h3>
        ....
      </Wrapper>
    );
  }

  if (!ats) {
    return (
      <Wrapper>
        <h3>Loading Ats data</h3>
        ....
      </Wrapper>
    );
  }

  const { open_questions: openQuestions = [] } = offer;
  const { config = {} } = ats;
  const { chatbotQuestions = [], formQuestions = [] } = config;

  const canRenderChatbotQuestions = chatbotQuestions.length > 0;
  const canRenderFormQuestions = formQuestions.length > 0;

  const mergedQuestions = buildMergedQuestions(openQuestions);

  return (
    <Wrapper>
      <FlexContainer>
        {canRenderFormQuestions && (
          <>
            <FlexRowContainer>
              <BoldTitle>{t("Qualification questions")}</BoldTitle>
              <BoldTitle>{t("Recruitee questions")}</BoldTitle>
            </FlexRowContainer>
            {renderQuestionsAndDropdowns(
              mergedQuestions,
              formQuestions,
              QUESTIONS_TYPES.FORM_QUESTION
            )}
          </>
        )}

        {canRenderChatbotQuestions && (
          <>
            <FlexRowContainer>
              <BoldTitle>{t("Screening questions")}</BoldTitle>
            </FlexRowContainer>
            {renderQuestionsAndDropdowns(
              mergedQuestions,
              chatbotQuestions,
              QUESTIONS_TYPES.CHATBOT_QUESTION
            )}
          </>
        )}
      </FlexContainer>
      <ContinueButton onClick={onSaveMappingQuestion}>{t("Save mappings")}</ContinueButton>
    </Wrapper>
  );
};
